// src/pages/campaigns/forms/CampaignTeamForm.jsx
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Typography,
  Card,
  CardContent,
  CardHeader,
  CardActions,
  Button,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  ListItemSecondary,
  Box,
  Chip,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import {
  Person as PersonIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PersonAdd as AddPersonIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  SupervisorAccount as ManagerIcon,
  Create as CreatorIcon,
  Visibility as ObserverIcon,
  Analytics as AnalystIcon,
  HowToReg as ApproverIcon,
  Settings as AdminIcon,
  LocalAtm as FinanceIcon,
  Business as ClientIcon,
  Groups as TeamIcon,
  Info as InfoIcon,
  Assignment as RolesIcon,
  Lightbulb as TipsIcon,
} from '@mui/icons-material';

// Import FormSection component
import { FormSection } from '../../../design-system/components/forms';

// Import service for fetching users
import * as userService from '../../../services/userService';

// Team role options - mapped from user.model.js roles
const CAMPAIGN_ROLES = [
  { value: 'manager', label: 'Campaign Manager', icon: <ManagerIcon />, description: 'Oversees the entire campaign with full permissions' },
  { value: 'creator', label: 'Content Creator', icon: <CreatorIcon />, description: 'Creates content for the campaign' },
  { value: 'approver', label: 'Content Approver', icon: <ApproverIcon />, description: 'Reviews and approves campaign content' },
  { value: 'analyst', label: 'Analyst', icon: <AnalystIcon />, description: 'Monitors campaign performance and metrics' },
  { value: 'observer', label: 'Observer', icon: <ObserverIcon />, description: 'Has view-only access to the campaign' },
];

// Map of user roles to likely campaign roles
const USER_ROLE_MAPPING = {
  'superadmin': ['manager', 'approver', 'analyst'],
  'admin': ['manager', 'approver', 'analyst'],
  'agency-admin': ['manager', 'approver', 'analyst'],
  'agency-manager': ['manager', 'approver', 'analyst'],
  'content-creator': ['creator'],
  'content-approver': ['approver'],
  'finance-manager': ['analyst'],
  'client-admin': ['manager', 'approver', 'analyst'],
  'client-manager': ['manager', 'approver'],
  'client-user': ['observer'],
  'user': ['observer']
};

// Role icon map based on campaign role
const getRoleIcon = (role) => {
  const roleOption = CAMPAIGN_ROLES.find(option => option.value === role);
  return roleOption ? roleOption.icon : <PersonIcon />;
};

// Initial member form
const initialMemberForm = {
  user: '',
  role: 'observer',
  permissions: {
    canEdit: false,
    canApprove: false,
    canManageBudget: false
  }
};

// CampaignTeamForm component
const CampaignTeamForm = ({
  campaignData,
  onChange,
  errors = {},
  touched = {}
}) => {
  const theme = useTheme();
  const [teamMembers, setTeamMembers] = useState(campaignData.team || []);
  const [openDialog, setOpenDialog] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [memberForm, setMemberForm] = useState({...initialMemberForm});
  const [users, setUsers] = useState([]);
  const [selectedUserRole, setSelectedUserRole] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Load users on component mount
  useEffect(() => {
    const loadUsers = async () => {
      setIsLoading(true);
      try {
        const response = await userService.getUsers();
        setUsers(response.data.users);
      } catch (error) {
        console.log('Failed to load users:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Initialize teamMembers state when campaignData.team changes
  useEffect(() => {
    // Only update if the campaignData.team is different from current teamMembers
    // This prevents an infinite loop
    if (JSON.stringify(campaignData.team) !== JSON.stringify(teamMembers)) {
      setTeamMembers(campaignData.team || []);
    }
  }, [campaignData.team]);

  // Handle team members changes and notify parent component
  const handleTeamMembersChange = (newTeamMembers) => {
    setTeamMembers(newTeamMembers);
    // Notify parent component of the change
    onChange('team', newTeamMembers);
  };

  // Open dialog to add new member
  const handleAddMember = () => {
    setMemberForm({...initialMemberForm});
    setSelectedUserRole('');
    setEditIndex(null);
    setOpenDialog(true);
  };

  // Open dialog to edit member
  const handleEditMember = (index) => {
    const member = teamMembers[index];
    setMemberForm({
      user: member.user._id || member.user,
      role: member.role,
      permissions: { ...member.permissions }
    });
    
    // Set the selected user role if user details are available
    if (member.user && member.user.role) {
      setSelectedUserRole(member.user.role);
    } else {
      setSelectedUserRole('');
    }
    
    setEditIndex(index);
    setOpenDialog(true);
  };

  // Delete team member
  const handleDeleteMember = (index) => {
    const updatedMembers = [...teamMembers];
    updatedMembers.splice(index, 1);
    handleTeamMembersChange(updatedMembers);
  };

  // Close member dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setMemberForm({...initialMemberForm});
    setSelectedUserRole('');
    setEditIndex(null);
  };

  // Handle form field changes
  const handleFormChange = (e) => {
    const { name, value, checked } = e.target;
    if (name.startsWith('permissions.')) {
      const permissionName = name.split('.')[1];
      setMemberForm(prev => ({
        ...prev,
        permissions: {
          ...prev.permissions,
          [permissionName]: checked
        }
      }));
    } else {
      setMemberForm(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // If user changes, set the user role and suggest permissions
    if (name === 'user') {
      const selectedUser = users.find(user => user._id === value);
      if (selectedUser) {
        setSelectedUserRole(selectedUser.role);
        
        // Suggest a campaign role based on user role
        const userRole = selectedUser.role;
        const suggestedRoles = USER_ROLE_MAPPING[userRole] || ['observer'];
        setMemberForm(prev => ({
          ...prev,
          role: suggestedRoles[0] || 'observer'
        }));
        
        // Auto-assign permissions based on suggested role
        handleRolePermissions(suggestedRoles[0]);
      }
    }

    // Auto-assign permissions based on role
    if (name === 'role') {
      handleRolePermissions(value);
    }
  };
  
  // Handle role-based permissions
  const handleRolePermissions = (role) => {
    let permissions = { canEdit: false, canApprove: false, canManageBudget: false };
    
    switch(role) {
      case 'manager':
        permissions = { canEdit: true, canApprove: true, canManageBudget: true };
        break;
      case 'creator':
        permissions = { canEdit: true, canApprove: false, canManageBudget: false };
        break;
      case 'approver':
        permissions = { canEdit: false, canApprove: true, canManageBudget: false };
        break;
      case 'analyst':
        permissions = { canEdit: false, canApprove: false, canManageBudget: true };
        break;
      case 'observer':
      default:
        // Observer has no special permissions
        break;
    }
    
    setMemberForm(prev => ({
      ...prev,
      permissions
    }));
  };

  // Save team member
  const handleSaveMember = () => {
    // Validate required fields
    if (!memberForm.user || !memberForm.role) {
      return;
    }

    // Find selected user
    const selectedUser = users.find(user => user._id === memberForm.user);
    if (!selectedUser) {
      return;
    }

    const newMember = {
      user: selectedUser,
      role: memberForm.role,
      permissions: memberForm.permissions
    };

    let updatedMembers;
    if (editIndex !== null) {
      // Update existing member
      updatedMembers = [...teamMembers];
      updatedMembers[editIndex] = newMember;
    } else {
      // Add new member
      updatedMembers = [...teamMembers, newMember];
    }

    handleTeamMembersChange(updatedMembers);
    handleCloseDialog();
  };

  // Get appropriate roles for a user based on their system role
  const getAppropriateRoles = (userRole) => {
    return USER_ROLE_MAPPING[userRole] || ['observer'];
  };

  // User role badge component
  const UserRoleBadge = ({ role }) => {
    let color;
    switch(role) {
      case 'superadmin':
      case 'admin':
        color = 'error';
        break;
      case 'agency-admin':
      case 'agency-manager':
        color = 'primary';
        break;
      case 'client-admin':
      case 'client-manager':
        color = 'secondary';
        break;
      case 'content-creator':
      case 'content-approver':
        color = 'info';
        break;
      case 'finance-manager':
        color = 'warning';
        break;
      default:
        color = 'default';
    }
    
    return (
      <Chip 
        label={role} 
        size="small" 
        color={color}
        sx={{ ml: 1, fontSize: '0.7rem' }}
      />
    );
  };

  // Get user role icon
  const getUserRoleIcon = (role) => {
    switch(role) {
      case 'superadmin':
      case 'admin':
        return <AdminIcon />;
      case 'agency-admin':
      case 'agency-manager':
        return <ManagerIcon />;
      case 'client-admin':
      case 'client-manager':
        return <ClientIcon />;
      case 'content-creator':
        return <CreatorIcon />;
      case 'content-approver':
        return <ApproverIcon />;
      case 'finance-manager':
        return <FinanceIcon />;
      default:
        return <PersonIcon />;
    }
  };

  // Function to render team members list
  const renderTeamMembersList = () => (
    <Card variant="outlined">
      <CardHeader 
        title="Team Members" 
        action={
          <Button
            variant="contained"
            startIcon={<AddPersonIcon />}
            onClick={handleAddMember}
          >
            Add Team Member
          </Button>
        }
      />
      <Divider />
      <CardContent>
        {teamMembers.length === 0 ? (
          <Box sx={{ 
            p: 3, 
            textAlign: 'center', 
            bgcolor: alpha(theme.palette.background.default, 0.5),
            borderRadius: 1,
            border: `1px dashed ${theme.palette.divider}`
          }}>
            <Typography color="textSecondary" gutterBottom>
              No team members assigned yet
            </Typography>
            <Typography variant="body2" color={errors?.team ? "error" : "textSecondary"} sx={{ fontWeight: errors?.team ? 'bold' : 'normal' }}>
              {errors?.team || "Add team members to collaborate on this campaign"}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddPersonIcon />}
              onClick={handleAddMember}
              sx={{ mt: 2 }}
            >
              Add Team Member
            </Button>
          </Box>
        ) : (
          <List>
            {teamMembers.map((member, index) => (
              <React.Fragment key={index}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  secondaryAction={
                    <Box>
                      <IconButton edge="end" onClick={() => handleEditMember(index)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton edge="end" onClick={() => handleDeleteMember(index)}>
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemAvatar>
                    <Avatar 
                      src={member.user.photo}
                      alt={member.user.firstName}
                      sx={{ 
                        bgcolor: member.role === 'manager' ? 'primary.main' :
                                member.role === 'approver' ? 'success.main' :
                                member.role === 'creator' ? 'info.main' :
                                member.role === 'analyst' ? 'warning.main' : 'grey.500'
                      }}
                    >
                      {getRoleIcon(member.role)}
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
                        <Typography>{member.user.firstName} {member.user.lastName}</Typography>
                        <Tooltip title="System Role">
                          <UserRoleBadge role={member.user.role} />
                        </Tooltip>
                        <Tooltip title="Campaign Role">
                          <Chip 
                            label={CAMPAIGN_ROLES.find(r => r.value === member.role)?.label || member.role} 
                            size="small"
                            color={
                              member.role === 'manager' ? 'primary' :
                              member.role === 'approver' ? 'success' :
                              member.role === 'creator' ? 'info' :
                              member.role === 'analyst' ? 'warning' : 'default'
                            }
                          />
                        </Tooltip>
                      </Box>
                    }
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        {member.permissions.canEdit && (
                          <Chip 
                            label="Can Edit" 
                            size="small" 
                            variant="outlined" 
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {member.permissions.canApprove && (
                          <Chip 
                            label="Can Approve" 
                            size="small" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                        {member.permissions.canManageBudget && (
                          <Chip 
                            label="Can Manage Budget" 
                            size="small" 
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    }
                  />
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </CardContent>
    </Card>
  );

  // Function to render role descriptions
  const renderRoleDescriptions = () => (
    <>
      <Grid container spacing={2}>
        {CAMPAIGN_ROLES.map((role) => (
          <Grid item xs={12} sm={6} md={4} key={role.value}>
            <Card variant="outlined" sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Avatar 
                    sx={{ 
                      mr: 1, 
                      bgcolor: role.value === 'manager' ? 'primary.main' :
                              role.value === 'approver' ? 'success.main' :
                              role.value === 'creator' ? 'info.main' :
                              role.value === 'analyst' ? 'warning.main' : 'grey.500'
                    }}
                  >
                    {role.icon}
                  </Avatar>
                  <Typography variant="subtitle1">
                    {role.label}
                  </Typography>
                </Box>
                <Typography variant="body2" color="textSecondary">
                  {role.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </>
  );

  // Function to render team management tips
  const renderTeamManagementTips = () => (
    <Box sx={{ 
      p: 2, 
      bgcolor: alpha(theme.palette.info.main, 0.1), 
      borderRadius: 1
    }}>
      <Typography variant="subtitle2" gutterBottom>
        Team Management Tips:
      </Typography>
      <Typography variant="body2">
        • System roles determine what a user can access in the platform
      </Typography>
      <Typography variant="body2">
        • Campaign roles define responsibilities specific to this campaign
      </Typography>
      <Typography variant="body2">
        • Assign at least one Campaign Manager for oversight
      </Typography>
      <Typography variant="body2">
        • Consider having separate Creators and Approvers for better quality control
      </Typography>
      <Typography variant="body2">
        • Limit budget management permissions to appropriate team members
      </Typography>
    </Box>
  );

  return (
    <>
      {/* Section 1: Team Members */}
      <FormSection
        title="Campaign Team Members"
        description="Assign team members to collaborate on this campaign."
        icon={<TeamIcon />}
        tooltip="Add the people who will work on this campaign and define their roles."
      >
        {renderTeamMembersList()}
      </FormSection>

      {/* Section 2: Role Descriptions */}
      <FormSection
        title="Campaign Role Descriptions"
        description="Understanding the different roles available for team members."
        icon={<RolesIcon />}
        tooltip="Learn what each role means and the permissions they typically have."
      >
        {renderRoleDescriptions()}
      </FormSection>

      {/* Section 3: Team Management Best Practices */}
      <FormSection
        title="Team Management Best Practices"
        description="Tips for effectively managing your campaign team."
        icon={<TipsIcon />}
        tooltip="Follow these guidelines to build an effective team structure for your campaign."
      >
        {renderTeamManagementTips()}
      </FormSection>

      {/* Team Member Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editIndex !== null ? "Edit Team Member" : "Add Team Member"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {/* User Select */}
            <Grid item xs={12}>
              <FormControl fullWidth disabled={isLoading}>
                <InputLabel id="user-label">Team Member</InputLabel>
                <Select
                  labelId="user-label"
                  id="user"
                  name="user"
                  value={memberForm.user}
                  onChange={handleFormChange}
                  label="Team Member"
                  required
                >
                  {users.map((user) => (
                    <MenuItem key={user._id} value={user._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
                        <Avatar 
                          src={user.photo} 
                          sx={{ width: 24, height: 24, mr: 1 }}
                        >
                          {getUserRoleIcon(user.role)}
                        </Avatar>
                        <Typography>{user.firstName} {user.lastName}</Typography>
                        <Typography 
                          variant="caption" 
                          color="textSecondary" 
                          sx={{ ml: 'auto' }}
                        >
                          {user.role}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Selected User System Role */}
            {selectedUserRole && (
              <Grid item xs={12}>
                <Box sx={{ 
                  p: 1.5, 
                  bgcolor: alpha(theme.palette.primary.main, 0.05),
                  borderRadius: 1,
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                    System Role:
                  </Typography>
                  <UserRoleBadge role={selectedUserRole} />
                  
                  <Typography variant="body2" color="textSecondary" sx={{ ml: 'auto' }}>
                    Suggested Roles:
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 0.5, ml: 1 }}>
                    {getAppropriateRoles(selectedUserRole).map(role => (
                      <Chip 
                        key={role} 
                        label={role} 
                        size="small"
                        variant="outlined"
                        sx={{ height: 20, fontSize: '0.7rem' }}
                      />
                    ))}
                  </Box>
                </Box>
              </Grid>
            )}

            {/* Campaign Role Select */}
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel id="role-label">Campaign Role</InputLabel>
                <Select
                  labelId="role-label"
                  id="role"
                  name="role"
                  value={memberForm.role}
                  onChange={handleFormChange}
                  label="Campaign Role"
                  required
                >
                  {CAMPAIGN_ROLES.map((option) => (
                    <MenuItem 
                      key={option.value} 
                      value={option.value}
                      disabled={selectedUserRole && !getAppropriateRoles(selectedUserRole).includes(option.value)}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Box sx={{ mr: 1 }}>{option.icon}</Box>
                        {option.label}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Permissions */}
            <Grid item xs={12}>
              <Typography variant="subtitle2" gutterBottom>
                Campaign Permissions
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions.canEdit}
                    onChange={handleFormChange}
                    name="permissions.canEdit"
                  />
                }
                label="Can edit campaign content"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions.canApprove}
                    onChange={handleFormChange}
                    name="permissions.canApprove"
                  />
                }
                label="Can approve campaign content"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={memberForm.permissions.canManageBudget}
                    onChange={handleFormChange}
                    name="permissions.canManageBudget"
                  />
                }
                label="Can manage campaign budget"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveMember} 
            variant="contained" 
            startIcon={<CheckIcon />}
            disabled={!memberForm.user || !memberForm.role}
          >
            {editIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CampaignTeamForm;